import { useState } from "react";
import { NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { HealthProvider, useHealth } from "@/context/HealthContext";
import { HEALTH_LANGS } from "@/lib/healthI18n";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  LayoutDashboard, Users, BrainCircuit, ClipboardCheck, GitBranch,
  ScrollText, PlaySquare, LogOut, Activity, ShieldCheck, Languages,
  ClipboardList, Lock, Milestone,
} from "lucide-react";

const NAV = [
  { to: "/health", key: "nav_overview", icon: LayoutDashboard, end: true },
  { to: "/health/patients", key: "nav_patients", icon: Users },
  { to: "/health/ai-core", key: "nav_aicore", icon: BrainCircuit },
  { to: "/health/review", key: "nav_review", icon: ClipboardCheck },
  { to: "/health/human-loop", key: "nav_hitl", icon: GitBranch },
  { to: "/health/audit", key: "nav_audit", icon: ScrollText },
  { to: "/health/presentation", key: "nav_presentation", icon: PlaySquare },
];

const PHASE2 = [
  { to: "/health/validation", key: "nav_validation", icon: ClipboardList },
  { to: "/health/governance", key: "nav_governance", icon: Lock },
  { to: "/health/roadmap", key: "nav_roadmap", icon: Milestone },
];

function TopBar() {
  const { ht, lang, setLang, overview } = useHealth();
  return (
    <header className="h-16 border-b border-slate-800 bg-[#071016] flex items-center justify-between px-6 shrink-0">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="flex items-center gap-2 px-2.5 py-1 rounded-sm bg-amber-950/40 border border-amber-800 text-amber-300 text-[11px] font-semibold uppercase tracking-widest" data-testid="health-demo-banner">
          <span className="h-2 w-2 rounded-full bg-amber-500 ngx-pulse" /> {ht("demoEnv")}
        </span>
        <span className="text-[11px] font-mono text-slate-500">{ht("syntheticOnly")} · {ht("notClinical")}</span>
      </div>
      <div className="flex items-center gap-4">
        {overview && (
          <span className="hidden md:flex items-center gap-2 text-xs font-mono text-slate-400">
            <ClipboardCheck className="h-3.5 w-3.5 text-amber-400" /> {overview.cases_requiring_review} · {ht("casesRequiringReview")}
          </span>
        )}
        <Languages className="h-4 w-4 text-slate-500" />
        <Select value={lang} onValueChange={setLang}>
          <SelectTrigger data-testid="health-language-select" className="h-9 w-36 bg-[#0A1620] border-slate-700 text-slate-200">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0A1620] border-slate-700 text-slate-100">
            {HEALTH_LANGS.map((l) => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </header>
  );
}

function Sidebar() {
  const { ht } = useHealth();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const doLogout = async () => { await logout(); navigate("/health/login"); };
  return (
    <aside className="w-64 shrink-0 bg-[#071016] border-r border-slate-800 flex flex-col">
      <div className="h-16 flex items-center gap-3 px-5 border-b border-slate-800">
        <div className="h-9 w-9 rounded-md bg-teal-600 flex items-center justify-center">
          <Activity className="h-5 w-5 text-white" />
        </div>
        <div>
          <div className="font-display text-base font-bold leading-none">NeuNexus</div>
          <div className="text-[10px] uppercase tracking-widest text-teal-400/80 mt-0.5">Health Intelligence</div>
        </div>
      </div>
      <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-y-auto">
        {NAV.map(({ to, key, icon: Icon, end }) => (
          <NavLink key={to} to={to} end={end} data-testid={`hnav-${key}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 h-11 rounded-md text-sm font-medium transition-colors ${
                isActive ? "bg-teal-600/15 text-teal-300 border-l-2 border-teal-500"
                  : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/40 border-l-2 border-transparent"
              }`}>
            <Icon className="h-[18px] w-[18px]" /> {ht(key)}
          </NavLink>
        ))}
        <div className="mt-4 pt-4 border-t border-slate-800">
          <div className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-violet-400/70">{ht("phase2Label")}</div>
          {PHASE2.map(({ to, key, icon: Icon }) => (
            <NavLink key={to} to={to} data-testid={`hnav-${key}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 h-11 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "bg-teal-600/15 text-teal-300 border-l-2 border-teal-500"
                    : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/40 border-l-2 border-transparent"
                }`}>
              <Icon className="h-[18px] w-[18px]" /> {ht(key)}
            </NavLink>
          ))}
        </div>
      </nav>
      <div className="p-3 border-t border-slate-800">
        <div className="flex items-center gap-3 px-3 py-2.5 rounded-md bg-[#0A1620] border border-slate-800">
          <div className="h-9 w-9 rounded-full bg-teal-800/60 flex items-center justify-center text-xs font-bold text-teal-200">
            {(user?.name || "DR").split(" ").map((w) => w[0]).slice(0, 2).join("")}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-slate-100 truncate">{user?.name || "Professional"}</div>
            <div className="flex items-center gap-1.5 text-[11px] text-teal-400/90">
              <ShieldCheck className="h-3 w-3" /> {ht("role_" + (user?.role || "healthcare_professional"))}
            </div>
          </div>
          <button onClick={doLogout} data-testid="health-logout" className="text-slate-500 hover:text-red-400">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}

function Inner() {
  return (
    <div className="h-screen flex bg-[#050C11] text-slate-100 overflow-hidden">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <TopBar />
        <main className="flex-1 overflow-y-auto p-6"><Outlet /></main>
      </div>
    </div>
  );
}

export default function HealthLayout() {
  const { user } = useAuth();
  if (user === null)
    return <div className="min-h-screen flex items-center justify-center bg-[#050C11] text-slate-400 font-mono text-sm">Loading NeuNexus…</div>;
  if (user === false) return <Navigate to="/health/login" replace />;
  return (
    <HealthProvider>
      <Inner />
    </HealthProvider>
  );
}
